import {
  PermissionOrganization,
  PermissionUser,
  UseAuthorizationDataQuery,
  UseAuthorizationDataQueryVariables,
} from '@/generated/graphql'
import { computed, ref, watch } from 'vue'
import { currentOrganization } from '@/app'
import gql from 'graphql-tag'
import { apolloClient } from '@/apollo'
import { authentication } from '@/authentication'

type OrgPermissionMap = { [key in PermissionOrganization]?: PermissionOrganization[] }
const permissionHierarchy = ref<OrgPermissionMap>({})
const userPermissions = ref<PermissionUser[]>([])
const orgPermissions = ref<PermissionOrganization[]>([])

const updatePermissions = () =>
  apolloClient
    .query<UseAuthorizationDataQuery, UseAuthorizationDataQueryVariables>({
      query: gql`
        query UseAuthorizationData {
          account {
            orgPermissionsHierarchy {
              permission
              reachable
            }
            me {
              userPermissions
              orgPermissions
            }
          }
        }
      `,
    })
    .then((result) => {
      const newHierarchy: OrgPermissionMap = {}
      result.data.account.orgPermissionsHierarchy.forEach((p) => {
        newHierarchy[p.permission] = p.reachable
      })
      permissionHierarchy.value = newHierarchy

      userPermissions.value = result.data.account.me?.userPermissions || []
      if (userPermissions.value.includes(PermissionUser.UserGlobalAdmin)) {
        orgPermissions.value = Object.values(PermissionOrganization)
        return
      }

      const newOrgPermissions: PermissionOrganization[] = []
      const myPermissions = result.data.account.me?.orgPermissions || []
      myPermissions
        .flatMap((p) => newHierarchy[p] || [])
        .forEach((p) => {
          if (!newOrgPermissions.includes(p)) {
            newOrgPermissions.push(p)
          }
        })
      orgPermissions.value = newOrgPermissions
    })
watch(
  [authentication, currentOrganization],
  () => {
    if (!authentication.value) {
      return
    }

    updatePermissions()
  },
  { immediate: true },
)

const hasUserAuthz = (...role: PermissionUser[]) =>
  role.reduce((acc, r) => acc && userPermissions.value.includes(r), true)
const reactiveUserAuthz = (...role: PermissionUser[]) =>
  computed(() => role.reduce((acc, r) => acc && userPermissions.value.includes(r), true))

const hasOrgAuthz = (...role: PermissionOrganization[]) =>
  role.reduce((acc, r) => acc && orgPermissions.value.includes(r), true)
const reactiveOrgAuthz = (...role: PermissionOrganization[]) =>
  computed(() => role.reduce((acc, r) => acc && orgPermissions.value.includes(r), true))

const canAccessOperatorHome = () =>
  hasOrgAuthz(PermissionOrganization.OrgProductRead) ||
  hasOrgAuthz(PermissionOrganization.OrgProductionBatchRead)

export default function useAuthorization() {
  return {
    hasUserAuthz,
    reactiveUserAuthz,
    hasOrgAuthz,
    reactiveOrgAuthz,
    canAccessOperatorHome,
    permissionHierarchy,
  }
}
